import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { socketService } from '../services/socketService';
import type { RootState } from './index';
import {
  jobDone,
  jobFailed,
  jobProgress,
  jobStarted,
  documentJobStarted,
  documentJobProgress,
  documentJobDone,
  documentJobFailed,
  type DocumentJobType,
  type ReportsJobType,
} from './jobSlice';
import { setUserBalance } from './authSlice';
import { addNotification } from './notificationSlice';
import { setAntiplagiatDocumentsInvalidated } from './uiSlice';
import { consumeFreeRewrite, setFreeRewriteUploadInfo } from './freeRewriteSlice';
import { setPsychGenerationStatus } from './psychologySlice';
import { patchPaperJob } from './paperWriteSlice';
import { setFeatureFlagsState } from './featureFlagsSlice';
import { applyBanner } from './bannerSlice';
import {
  FEATURE_FLAG_LABELS,
  FEATURE_FLAG_DEFAULT_REASONS,
  type FeatureFlagsSocketEvent,
  type FeatureFlagKey,
} from '../types/featureFlags';
import type { BannerSocketPayload } from '../types/banner';
import {
  addAdminMessageFromSocket,
  markSupportThreadRead,
  setAgentTyping,
  threadUpdatedFromSocket,
} from './supportChatSlice';
import type {
  SupportSocketMessagePayload,
  SupportSocketThreadPayload,
} from '../types/support';

type OnlineUser = string | {
  userId?: string;
  id?: string;
  username?: string;
  email?: string;
  [key: string]: any;
};

/** Активная задача пользователя (для админ-панели) */
export interface AdminActiveJob {
  jobId: string;
  queue?: string;
  progress?: number;
  stage?: string;
  message?: string;
  sessionId?: string;
  status?: string;
}

interface SocketState {
  isConnected: boolean;
  isConnecting: boolean;
  connectionError: string | null;
  lastActivity: string | null;
  onlineUsers: OnlineUser[];
  /** Активные задачи по userId (только для админов, обновляется из doc-event) */
  adminActiveJobs: Record<string, AdminActiveJob[]>;
}

const initialState: SocketState = {
  isConnected: false,
  isConnecting: false,
  connectionError: null,
  lastActivity: null,
  onlineUsers: [],
  adminActiveJobs: {},
};

const socketSlice = createSlice({
  name: 'socket',
  initialState,
  reducers: {
    setConnecting: (state, action: PayloadAction<boolean>) => {
      state.isConnecting = action.payload;
      if (action.payload) state.connectionError = null;
    },
    setConnected: (state, action: PayloadAction<boolean>) => {
      state.isConnected = action.payload;
      state.isConnecting = false;
      if (action.payload) {
        state.connectionError = null;
        state.lastActivity = new Date().toISOString();
      }
    },
    setConnectionError: (state, action: PayloadAction<string | null>) => {
      // Используй ЭТО только для фатальных ошибок/ручного отключения
      state.connectionError = action.payload;
      state.isConnecting = false;
    },
    setTransientError: (state, action: PayloadAction<string | null>) => {
      // Ошибка во время авто-реконнекта — НЕ сбрасываем флаг "пытаемся"
      state.connectionError = action.payload;
    },
    setLastActivity: (state, action: PayloadAction<string>) => {
      state.lastActivity = action.payload;
    },
    setOnlineUsers: (state, action: PayloadAction<OnlineUser[]>) => {
      state.onlineUsers = action.payload;
    },
    addAdminActiveJob: (state, action: PayloadAction<{ userId: string; job: AdminActiveJob }>) => {
      const { userId, job } = action.payload;
      if (!state.adminActiveJobs[userId]) state.adminActiveJobs[userId] = [];
      const idx = state.adminActiveJobs[userId].findIndex((j) => j.jobId === job.jobId || (job.sessionId && j.sessionId === job.sessionId));
      if (idx >= 0) state.adminActiveJobs[userId][idx] = { ...state.adminActiveJobs[userId][idx], ...job };
      else state.adminActiveJobs[userId].push(job);
    },
    updateAdminActiveJob: (state, action: PayloadAction<{ userId: string; jobId: string; updates: Partial<AdminActiveJob> }>) => {
      const { userId, jobId, updates } = action.payload;
      const jobs = state.adminActiveJobs[userId];
      if (!jobs) return;
      const j = jobs.find((x) => x.jobId === jobId);
      if (j) Object.assign(j, updates);
    },
    removeAdminActiveJob: (state, action: PayloadAction<{ userId: string; jobId?: string; sessionId?: string }>) => {
      const { userId, jobId, sessionId } = action.payload;
      const jobs = state.adminActiveJobs[userId];
      if (!jobs) return;
      state.adminActiveJobs[userId] = jobs.filter(
        (j) => (jobId ? j.jobId !== jobId : true) && (sessionId ? j.sessionId !== sessionId : true)
      );
      if (state.adminActiveJobs[userId].length === 0) delete state.adminActiveJobs[userId];
    },
    resetSocket: (state) => {
      state.isConnected = false;
      state.isConnecting = false;
      state.connectionError = null;
      state.lastActivity = null;
      state.onlineUsers = [];
      state.adminActiveJobs = {};
    },
  },
});

export const {
  setConnecting,
  setConnected,
  setConnectionError,
  setTransientError,
  setLastActivity,
  setOnlineUsers,
  addAdminActiveJob,
  updateAdminActiveJob,
  removeAdminActiveJob,
  resetSocket,
} = socketSlice.actions;

// Селекторы
export const selectSocket = (state: RootState) => state.socket;
export const selectIsSocketConnected = (state: RootState) => state.socket.isConnected;
export const selectIsSocketConnecting = (state: RootState) => state.socket.isConnecting;
export const selectSocketError = (state: RootState) => state.socket.connectionError;
export const selectOnlineUsers = (state: RootState) => state.socket.onlineUsers;
export const selectAdminActiveJobs = (state: RootState) => state.socket.adminActiveJobs;

const REWRITE_JOB_SUFFIXES = ["attach-report", "paraphrase", "parse"] as const;

const extractRewriteSlotId = (event: any): string | null => {
  if (typeof event?.slotId === "string" && event.slotId) {
    return event.slotId;
  }
  if (typeof event?.context?.rewriteSlotId === "string" && event.context.rewriteSlotId) {
    return event.context.rewriteSlotId;
  }
  const jobId = typeof event?.jobId === "string" ? event.jobId : "";
  if (!jobId) return null;

  for (const suffix of REWRITE_JOB_SUFFIXES) {
    const tail = `-${suffix}`;
    if (!jobId.endsWith(tail)) continue;
    const withoutTail = jobId.slice(0, -tail.length);
    if (!withoutTail.startsWith("u-")) continue;
    const marker = "-s-";
    const markerIdx = withoutTail.indexOf(marker, 2);
    if (markerIdx === -1) continue;
    const slotId = withoutTail.slice(markerIdx + marker.length);
    if (slotId) return slotId;
  }
  return null;
};

/** Переподключение с актуальным токеном из localStorage (после refresh). */
export const reconnectSocketWithCurrentToken = () => async (dispatch: any) => {
  const token = localStorage.getItem('accessToken');
  if (!token) return;

  if (socketService.getSocket()?.connected) {
    socketService.reconnectWithToken(token);
    dispatch(setConnected(true));
    dispatch(setConnectionError(null));
    return;
  }

  await dispatch(disconnectSocket());
  await dispatch(connectSocket(token));
};

export const connectSocket = (accessToken: string) => async (dispatch: any, getState: () => RootState) => {
  try {
    dispatch(setConnecting(true));
    await socketService.connect(accessToken);
    // Реальный handshake — в обработчике socket 'connect' ниже

    socketService.on('connected', (msg: any) => {
      dispatch(setLastActivity(new Date().toISOString()));
    });

    /**
     * Классифицируем очередь в пару (scope, type):
     *   - document scope: parse/paraphrase/attach-report(-from-ap) — храним per-slot
     *   - reports scope: antiplagiat/prepare-antiplagiat-doc/attach-antiplagiat-report — глобальные
     * Так мы гарантируем независимость слотов: событие прилетает с eventSlotId
     * и роутится в состояние именно этого слота, даже если пользователь
     * сейчас смотрит другой.
     */
    type QueueClassification =
      | { scope: 'document'; type: DocumentJobType }
      | { scope: 'reports'; type: ReportsJobType }
      | null;

    const classifyQueue = (queue: string): QueueClassification => {
      if (queue === 'parse' || queue === 'paraphrase') {
        return { scope: 'document', type: queue };
      }
      if (queue === 'attach-report' || queue === 'attach-report-from-ap') {
        return { scope: 'document', type: 'attachReport' };
      }
      if (queue === 'antiplagiat') return { scope: 'reports', type: 'antiplagiat' };
      if (queue === 'prepare-antiplagiat-doc')
        return { scope: 'reports', type: 'prepareAntiplagiatDoc' };
      if (queue === 'attach-antiplagiat-report')
        return { scope: 'reports', type: 'attachAntiplagiatReport' };
      return null;
    };

    const handleBalance = (e: any) => {
      /**
       * Пример:
       * {
       *   kind: "balance-updated",
       *   balance: 97500,
       *   delta: -2500,
       *   userId: "682409307",
       *   context: { reason: 'paraphrase', docNumber: 1, jobId: '...' },
       *   bonus?: number
       * }
       */
      if (typeof e?.balance === 'number') {
        // Обновляем баланс для всех событий, включая пополнение
        dispatch(setUserBalance({ balance: e.balance, bonus: e?.bonus }));

        const delta = typeof e?.delta === 'number' ? e.delta : null;
        if (delta !== null) {
          const isDeposit = delta > 0;
          const absDelta = Math.abs(delta);
          const title = isDeposit ? 'Баланс пополнен' : 'Списание с баланса';
          const reason = e?.context?.reason;
          const reasonLabel =
            reason === 'deposit'
              ? 'Пополнение'
              : reason === 'paraphrase'
              ? 'Перефразирование'
              : reason === 'attach-report'
              ? 'Отчет антиплагиат'
              : reason || '';

          const bonusAdded =
            (typeof e?.context?.bonusAdded === 'number' ? e.context.bonusAdded : 0) +
            (typeof e?.context?.referralBonus === 'number' ? e.context.referralBonus : 0);
          const bonusSuffix =
            isDeposit && bonusAdded > 0 ? ` (бонусы +${bonusAdded} ₽)` : '';
          const bonusBalance =
            typeof e?.bonus === 'number' ? `, Бонусы: ${e.bonus} ₽` : '';

          dispatch(
            addNotification({
              // Списание за действие, которое пользователь сам запустил, — не
              // предупреждение: спокойный info без звука. Серия изменений
              // баланса обновляет один и тот же тост.
              key: 'balance',
              type: isDeposit ? 'success' : 'info',
              title,
              message: `${reasonLabel ? reasonLabel + ': ' : ''}${
                isDeposit ? '+' : '-'
              }${absDelta} ₽${bonusSuffix} · Баланс: ${e.balance} ₽${bonusBalance}`,
              ttl: 8000,
            })
          );
        }
      }
    };

    socketService.on("online_users", (users: OnlineUser[]) => {
      dispatch(setOnlineUsers(users));
    });

    socketService.on('doc-event', (e: any) => {
      
      // Обработка баланса внутри doc-event
      if (e?.kind === 'balance-updated') {
        handleBalance(e);
        return;
      }

      // Список документов антиплагиата изменился — обновить список отчётов на фронте
      if (e?.kind === 'antiplagiat-documents-updated') {
        dispatch(setAntiplagiatDocumentsInvalidated(Date.now()));
        return;
      }

      // ====== Психология: прогресс генерации ======
      if (e?.kind === 'psychology-progress') {
        const eventUserId = e?.userId != null ? String(e.userId) : null;
        const isCurrentUser = eventUserId && getState().storeAuth?.user && String(getState().storeAuth.user.userId) === eventUserId;
        if (isCurrentUser) {
          dispatch(setPsychGenerationStatus({
            sessionStatus: e.status,
            generatorStatus: e.status === 'completed' || e.status === 'failed' ? null : 'running',
            progress: e.progress || null,
            error: e.status === 'failed' ? (e.error || 'Ошибка генерации') : null,
          }));

          // Пользователь мог уйти на другую вкладку/страницу сайта, пока шла генерация —
          // экран психологии тогда размонтирован и сам он завершение не покажет. Генерация на
          // бэкенде от этого не останавливается, поэтому здесь же (глобальный обработчик,
          // не привязанный к текущей странице) уведомляем тостом со звуком — но только если
          // пользователь и так не смотрит на этот экран, чтобы не дублировать уведомление.
          if ((e.status === 'completed' || e.status === 'failed') && !getState().psychology?.isPageActive) {
            dispatch(addNotification({
              type: e.status === 'completed' ? 'success' : 'error',
              title: e.status === 'completed' ? 'Психологическая работа готова' : 'Ошибка генерации',
              message: e.status === 'completed'
                ? 'Документ сгенерирован — можно скачать в разделе «Эмпирическое исследование».'
                : (e.error || e.progress || 'Не удалось сгенерировать документ'),
              ttl: 12000,
            }));
          }
        }
        if (eventUserId) {
          if (e.status === 'completed' || e.status === 'failed') {
            dispatch(removeAdminActiveJob({ userId: eventUserId, sessionId: e.sessionId }));
          } else {
            dispatch(addAdminActiveJob({
              userId: eventUserId,
              job: { jobId: e.sessionId || 'psy', sessionId: e.sessionId, queue: 'psychology', progress: e.progress, status: e.status },
            }));
          }
        }
        return;
      }

      // ====== Автонаписание рефератов/статей/эссе: прогресс генерации ======
      if (e?.kind === 'paper-write-progress') {
        const eventUserId = e?.userId != null ? String(e.userId) : null;
        const isCurrentUser = eventUserId && getState().storeAuth?.user && String(getState().storeAuth.user.userId) === eventUserId;
        const currentJob = getState().paperWrite?.job;
        if (isCurrentUser && currentJob && String(e.jobId) === String(currentJob.id)) {
          const isDone = e.status === 'completed' || e.status === 'failed';
          dispatch(patchPaperJob({
            id: currentJob.id,
            generatorStatus: isDone ? null : e.status,
            progress: e.progress || null,
            status: e.status === 'completed' ? 'completed' : e.status === 'failed' ? 'failed' : currentJob.status,
            error: e.status === 'failed' ? (e.progress || 'Ошибка генерации') : currentJob.error,
            hasFile: e.status === 'completed' ? true : currentJob.hasFile,
          }));
        }

        // Пользователь мог уйти на другую вкладку/страницу сайта, пока шла генерация — экран
        // автонаписания тогда размонтирован (и мог вообще не совпасть с currentJob выше — job
        // остаётся видимым в Redux и после ухода со страницы, но если пользователь тем временем
        // создал ДРУГОЙ job, currentJob уже не тот). Генерация на бэкенде не останавливается,
        // поэтому уведомляем тостом со звуком независимо от currentJob — но только если
        // пользователь не смотрит сейчас на этот экран, чтобы не дублировать уведомление.
        if (isCurrentUser && (e.status === 'completed' || e.status === 'failed') && !getState().paperWrite?.isPageActive) {
          dispatch(addNotification({
            type: e.status === 'completed' ? 'success' : 'error',
            title: e.status === 'completed' ? 'Документ готов' : 'Ошибка генерации',
            message: e.status === 'completed'
              ? 'Ваша работа сгенерирована — можно скачать в разделе «Автонаписание».'
              : (e.progress || 'Не удалось сгенерировать документ'),
            ttl: 12000,
          }));
        }
        if (eventUserId) {
          if (e.status === 'completed' || e.status === 'failed') {
            dispatch(removeAdminActiveJob({ userId: eventUserId, jobId: e.jobId }));
          } else {
            dispatch(addAdminActiveJob({
              userId: eventUserId,
              job: { jobId: e.jobId, queue: 'paper-write', progress: e.progress, status: e.status },
            }));
          }
        }
        return;
      }

      // ====== Бесплатный рерайт: доступен ======
      if (e?.kind === 'free-rewrite-available') {
        dispatch(setFreeRewriteUploadInfo({
          available: true,
          pages: e.pages,
          message: e.message,
        }));
        dispatch(addNotification({
          type: 'success',
          title: 'Бесплатный рерайт',
          message: e.message || `Вам доступен бесплатный рерайт первых ${e.pages} страниц!`,
          ttl: 10000,
        }));
        return;
      }

      // ====== Бесплатный рерайт: использован ======
      if (e?.kind === 'free-rewrite-consumed') {
        dispatch(consumeFreeRewrite({ pages: e.pages }));
        dispatch(addNotification({
          type: 'info',
          title: 'Бонус использован',
          message: e.message || `Бонус бесплатного рерайта (${e.pages} стр.) использован`,
          ttl: 6000,
        }));
        return;
      }

      // { kind: 'job-start' | 'job-progress' | 'job-done' | 'job-failed', ... }
      const classification = classifyQueue(e.queue);
      const eventUserId = e?.userId != null ? String(e.userId) : null;
      const isCurrentUser =
        eventUserId &&
        getState().storeAuth?.user &&
        String(getState().storeAuth.user.userId) === eventUserId;
      const eventSlotId = extractRewriteSlotId(e);
      const jobIdStr = e?.jobId != null ? String(e.jobId) : null;

      switch (e.kind) {
        case 'payment-succeeded':
          // Баланс уже обновляется через balance-updated, просто обновляем на всякий случай
          if (typeof e?.balance === 'number') {
            dispatch(setUserBalance({ balance: e.balance, bonus: e?.bonus }));
          }
          break;

        case 'job-start':
          if (classification && isCurrentUser && jobIdStr) {
            if (classification.scope === 'document' && eventSlotId) {
              // Независимые слоты: стейт хранится по slotId даже если юзер
              // сейчас смотрит другой слот — при возврате он увидит прогресс.
              dispatch(
                documentJobStarted({
                  slotId: eventSlotId,
                  type: classification.type,
                  jobId: jobIdStr,
                })
              );
            } else if (classification.scope === 'reports') {
              dispatch(jobStarted({ type: classification.type, jobId: jobIdStr }));
            }
          }
          if (eventUserId && jobIdStr) {
            dispatch(addAdminActiveJob({
              userId: eventUserId,
              job: { jobId: jobIdStr, queue: e.queue },
            }));
          }
          break;

        case 'job-progress':
          if (
            typeof e.progress === 'number' &&
            classification &&
            isCurrentUser
          ) {
            if (classification.scope === 'document' && eventSlotId) {
              dispatch(
                documentJobProgress({
                  slotId: eventSlotId,
                  type: classification.type,
                  progress: e.progress,
                })
              );
            } else if (classification.scope === 'reports') {
              dispatch(
                jobProgress({ type: classification.type, progress: e.progress })
              );
            }
          }
          if (eventUserId && jobIdStr) {
            dispatch(addAdminActiveJob({
              userId: eventUserId,
              job: { jobId: jobIdStr, queue: e.queue, progress: e.progress, stage: e.stage, message: e.message },
            }));
          }
          break;

        case 'job-done':
        case 'job-failed':
          if (classification && isCurrentUser) {
            if (classification.scope === 'document' && eventSlotId) {
              if (e.kind === 'job-done') {
                dispatch(
                  documentJobDone({
                    slotId: eventSlotId,
                    type: classification.type,
                    result: e.result || null,
                  })
                );
              } else {
                dispatch(
                  documentJobFailed({
                    slotId: eventSlotId,
                    type: classification.type,
                    error: e.error || e.message || 'Ошибка задачи',
                    errorCode: e.errorCode ?? null,
                    recoverable: e.recoverable ?? null,
                    errorDetails: e.details ?? null,
                  })
                );
              }
            } else if (classification.scope === 'reports') {
              if (e.kind === 'job-done') {
                dispatch(
                  jobDone({ type: classification.type, result: e.result || null })
                );
              } else {
                dispatch(
                  jobFailed({
                    type: classification.type,
                    error: e.error || e.message || 'Ошибка задачи',
                    errorCode: e.errorCode ?? null,
                    recoverable: e.recoverable ?? null,
                    errorDetails: e.details ?? null,
                  })
                );
              }
            }
          }
          if (eventUserId && jobIdStr) {
            dispatch(removeAdminActiveJob({ userId: eventUserId, jobId: jobIdStr }));
          }
          break;
      }
    });

    socketService.on('balance-updated', handleBalance);
    socketService.on('balance', handleBalance);

    /**
     * Чат поддержки: новое сообщение не от пользователя — от админа (`admin_to_user`) или
     * от AI-агента (`ai_to_user`, см. SUPPORT_CHAT_FRONTEND.md §7). Оба рендерятся одинаково.
     * См. SUPPORT_CHAT_FRONTEND.md §4.1.
     * Сообщение пушим в стейт. Если виджет уже открыт — сразу шлём read,
     * чтобы счётчик unread не «мигнул».
     */
    socketService.on('support:message', (payload: SupportSocketMessagePayload) => {
      if (!payload?.message) return;
      // Игнорируем эхо сообщений самого пользователя — они добавляются через ответ POST.
      if (payload.message.direction === 'user_to_admin') {
        if (payload.thread) {
          dispatch(threadUpdatedFromSocket(payload.thread));
        }
        return;
      }
      dispatch(addAdminMessageFromSocket(payload.message));
      if (payload.thread) {
        dispatch(threadUpdatedFromSocket(payload.thread));
      }
      // если виджет открыт — пометить прочитанным
      const isOpen = getState().supportChat?.isOpen;
      if (isOpen) {
        dispatch(markSupportThreadRead() as any);
      }
    });

    /**
     * Чат поддержки: AI-агент начал/закончил формировать ответ. Приходит перед вызовом LLM и
     * после (успех/ошибка/эскалация) — на фронте достаточно показать/убрать «печатает...», сам
     * ответ всё равно придёт отдельным `support:message`.
     */
    socketService.on('support:typing', (payload: { isTyping?: boolean }) => {
      dispatch(setAgentTyping(Boolean(payload?.isTyping)));
    });

    /**
     * Чат поддержки: тред изменился (status open/closed).
     * См. SUPPORT_CHAT_FRONTEND.md §3.2.
     */
    socketService.on('support:thread-updated', (payload: SupportSocketThreadPayload) => {
      if (!payload?.thread) return;
      dispatch(threadUpdatedFromSocket(payload.thread));
    });

    /**
     * Feature-флаги: приходит:
     *   1) сразу после connect (source: 'connect') — состояние из Redis;
     *   2) при любом изменении в админке (source: 'setFlag' | 'setAll') — бродкастом всем,
     *      включая гостей.
     * См. ADMIN_FEATURE_FLAGS_FRONTEND.md §4.
     */
    let lastNotifiedFlagState: Partial<Record<FeatureFlagKey, boolean>> = {};
    socketService.on('feature-flags', (event: FeatureFlagsSocketEvent) => {
      if (!event || !event.state) return;
      const prevState = getState().featureFlags.state;
      dispatch(
        setFeatureFlagsState({
          state: event.state,
          key: event.key ?? null,
          source: event.source ?? null,
          updatedBy: event.updatedBy ?? null,
        }),
      );

      // Toast только при реальном переключении админом (не при connect),
      // и только если значение действительно поменялось vs предыдущее состояние.
      if (event.source === 'connect') {
        lastNotifiedFlagState = {};
        return;
      }

      const keysToCheck: FeatureFlagKey[] = event.key
        ? [event.key]
        : (Object.keys(event.state) as FeatureFlagKey[]);

      for (const key of keysToCheck) {
        const next = event.state[key];
        if (!next) continue;
        const prevEnabled = prevState?.[key]?.enabled;
        // Игнорим первый раз (когда prev еще не было) и идентичные значения
        if (prevEnabled === undefined || prevEnabled === next.enabled) continue;
        if (lastNotifiedFlagState[key] === next.enabled) continue;
        lastNotifiedFlagState[key] = next.enabled;

        const label = FEATURE_FLAG_LABELS[key] ?? key;
        if (next.enabled) {
          dispatch(
            addNotification({
              type: 'success',
              title: `«${label}» снова доступен`,
              message: 'Функция включена обратно. Можно продолжать работу.',
              ttl: 6000,
            }),
          );
        } else {
          const reason = (next.reason && next.reason.trim()) || FEATURE_FLAG_DEFAULT_REASONS[key];
          dispatch(
            addNotification({
              type: 'warning',
              title: `«${label}» временно недоступен`,
              message: reason,
              ttl: 8000,
            }),
          );
        }
      }
    });

    /**
     * Глобальное объявление: приходит сразу после connect и при любом
     * create/update/delete в админке. Гости тоже получают событие, если сокет есть.
     * См. README_BANNER_FRONTEND.md §1.
     */
    socketService.on('banner', (payload: BannerSocketPayload) => {
      dispatch(
        applyBanner({
          banner: payload?.banner ?? null,
          source: 'socket',
        }),
      );
    });

    socketService.on('connect', () => {
      // console.log('✅ socket connected');
      dispatch(setConnected(true));
      dispatch(setLastActivity(new Date().toISOString()));
    });

    socketService.on('disconnect', (reason: any) => {
      // console.log('🔌 socket disconnected:', reason);
      dispatch(setConnected(false));
    });

    socketService.on('connect_error', (err: any) => {
      // console.warn('⚠️ connect_error (будем ретраить):', err?.message);
      dispatch(setTransientError(err?.message || 'connect_error'));
      // НЕ вызываем setConnectionError — иначе опять пойдут новые connect()
    });

  } catch (error) {
    const msg = error instanceof Error ? error.message : 'Неизвестная ошибка подключения';
    // console.error('❌ Ошибка подключения к Socket.IO:', msg);
    dispatch(setConnectionError(msg));
  }
};

// Thunk для отключения от Socket.IO
export const disconnectSocket = () => async (dispatch: any) => {
  try {
    // console.log('🔌 Отключение от Socket.IO...');
    socketService.disconnect();
    dispatch(resetSocket());
    // console.log('✅ Socket.IO отключен успешно');
  } catch (error) {
    // console.error('❌ Ошибка при отключении Socket.IO:', error);
  }
};

export default socketSlice.reducer;
